import { useContext, useEffect, useRef, useState } from 'react'
import {
  IS_TESTING_PAYMENTS,
  MERCADOPAGO_PAYMENT_PUBLIC_KEY,
  MERCADOPAGO_SUBSCRIPTION_PUBLIC_KEY,
  VALIDATION_AMOUNT
} from '../../../components/utils/constant'
import { ToastContext } from '../../../context/ToastContext'
import { useMutationSaveCard } from '../../../queries/paymentQueries'
import CustomButton from '../ui/CustomButton'

const CHECKOUT_STATE = {
  IDLE: 'idle',
  COLLECTING_CARD_DATA: 'collecting_card_data',
  CREATING_TOKEN: 'creating_token',
  SUBMITTING_SUBSCRIPTION: 'submitting_subscription',
  FAILED_REQUIRES_NEW_CVV: 'failed_requires_new_cvv',
  SUCCESS: 'success'
}

const TOKEN_CVV_ERROR_MESSAGE =
  'No se pudo validar la tarjeta. Ingresa nuevamente el CVV para reintentar.'

const generateFlowId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `flow_${Date.now()}_${Math.random().toString(16).slice(2)}`
}

const normalizeText = (value) => String(value || '').toLowerCase()

const isTokenOrCvvError = (error) => {
  const data = error?.response?.data
  const fields = [
    error?.code,
    error?.message,
    data?.code,
    data?.error,
    data?.status,
    data?.message,
    data?.cause,
    data?.details
  ]
  const haystack = normalizeText(fields.filter(Boolean).join(' '))
  return (
    haystack.includes('cc_val_433') ||
    haystack.includes('cc_rejected_bad_filled_security_code') ||
    ((haystack.includes('token') || haystack.includes('cvv') || haystack.includes('security code')) &&
      (haystack.includes('invalid') || haystack.includes('expired') || haystack.includes('vencid')))
  )
}

const pickPreapprovalToken = (formData) => {
  if (!formData || typeof formData !== 'object') return undefined
  const raw =
    formData.preapproval_token ??
    formData.preapprovalToken ??
    formData.preapproval_id
  if (raw == null || raw === '') return undefined
  const trimmedPreapprovalToken = String(raw).trim()
  return trimmedPreapprovalToken || undefined
}

const getErrorMessage = (error) => {
  if (!error) return 'No se pudo completar. Intenta de nuevo.'
  const data = error?.response?.data
  if (data != null && typeof data === 'object' && typeof data.message === 'string') return data.message
  if (typeof data === 'string') return data
  if (typeof error?.message === 'string') return error.message
  return 'No se pudo completar. Intenta de nuevo.'
}

const formatInitializationSettings = (amount, customerId, cardsIds, showSavedCard) => {
  const amountNum = IS_TESTING_PAYMENTS ? 950 : Number(amount)
  const useSavedCards = showSavedCard && customerId && Array.isArray(cardsIds) && cardsIds.length > 0
  if (!useSavedCards) {
    return { amount: amountNum }
  }
  return {
    amount: amountNum,
    payer: { customerId, cardsIds }
  }
}

const normalizePaymentTypeId = (value) => {
  if (value == null) return null

  const normalizedValue = String(value).trim().toLowerCase()

  if (!normalizedValue) return null
  if (normalizedValue.includes('credit')) return 'credit_card'
  if (normalizedValue.includes('debit')) return 'debit_card'

  return normalizedValue
}

const buildPaymentMethodsCustomization = (paymentTypeId) => {
  const normalizedPaymentTypeId = normalizePaymentTypeId(paymentTypeId)

  if (normalizedPaymentTypeId === 'credit_card') {
    return {
      creditCard: 'all',
      maxInstallments: 1
    }
  }

  if (normalizedPaymentTypeId === 'debit_card') {
    return {
      debitCard: 'all',
      maxInstallments: 1
    }
  }

  return {
    creditCard: 'all',
    debitCard: 'all',
    maxInstallments: 1
  }
}

const SUBMIT_TIMEOUT_MS = 90000

const withTimeout = (promise, ms, message) => {
  let timeoutId
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(message)), ms)
  })
  return Promise.race([promise, timeoutPromise]).finally(() => clearTimeout(timeoutId))
}

const clearBrickContainer = (containerNode) => {
  if (containerNode) containerNode.innerHTML = ''
}

const getFirstSavedCardId = (cardsIds = []) =>
  cardsIds.find(id => typeof id === 'string' && id.trim() !== '')?.trim() || ''

const createPaymentBrickSettings = ({
  amount,
  customerId,
  cardsIds,
  showSavedCard,
  paymentTypeId,
  saveCard,
  submitLabel,
  cardNameRef,
  onSuccess,
  onProcessingChange,
  setShowSuccessMessage,
  setIsProcessing,
  setCheckoutState,
  setBrickResetKey,
  flowType,
  showToast,
  saveCardMutation,
  onSaveCardRequest,
  managedByParent = false,
  submitLockRef,
  activeRequestControllerRef
}) => {
  const initialization = formatInitializationSettings(amount, customerId, cardsIds, showSavedCard)
  const effectiveSubmitLabel = submitLabel || (saveCard ? 'Agregar Tarjeta' : 'Continuar')

  const clearProcessing = () => {
    setIsProcessing(false)
    onProcessingChange?.(false)
    submitLockRef.current = false
  }

  return {
    initialization,
    customization: {
      visual: {
        style: {
        customVariables: {
          textPrimaryColor: '#1e293b',
          textSecondaryColor: '#475569',
          inputBackgroundColor: '#ffffff',
          formBackgroundColor: '#ffffff',

          baseColor: '#1CB2AF',
          baseColorFirstVariant: '#1CB2AF',
          baseColorSecondVariant: '#7c3aed',

          errorColor: '#FF3A0B',
          successColor: '#1CB2AF',

          outlinePrimaryColor: '#bfdbfe',
          outlineSecondaryColor: '#e2e8f0',

          buttonTextColor: '#ffffff',

          fontSizeExtraSmall: '11px',
          fontSizeSmall: '12px',
          fontSizeMedium: '14px',
          fontSizeLarge: '16px',
          fontSizeExtraLarge: '20px',

          fontWeightNormal: '400',
          fontWeightSemiBold: '600',

          formInputsTextTransform: 'none',

          inputVerticalPadding: '13px',
          inputHorizontalPadding: '16px',

          inputFocusedBoxShadow: '0 0 0 3px rgba(37, 99, 235, 0.15)',
          inputErrorFocusedBoxShadow: '0 0 0 3px rgba(220, 38, 38, 0.14)',

          inputBorderWidth: '1px',
          inputFocusedBorderWidth: '1px',

          borderRadiusSmall: '12px',
          borderRadiusMedium: '18px',
          borderRadiusLarge: '24px',
          borderRadiusFull: '999px',

          formPadding: '20px'
        }
      },
        texts: { formSubmit: effectiveSubmitLabel }
      },
      paymentMethods: buildPaymentMethodsCustomization(paymentTypeId)
    },
    callbacks: {
      onReady: () => {
        setCheckoutState(CHECKOUT_STATE.COLLECTING_CARD_DATA)
      },
      onSubmit: async ({ formData, selectedPaymentMethod }) => {
        if (submitLockRef.current) {
          return Promise.reject(new Error('Ya existe una operación en curso.'))
        }
        submitLockRef.current = true
        setIsProcessing(true)
        onProcessingChange?.(true)
        setCheckoutState(CHECKOUT_STATE.CREATING_TOKEN)

        const flowId = generateFlowId()
        activeRequestControllerRef.current?.abort?.()
        activeRequestControllerRef.current = typeof AbortController !== 'undefined'
          ? new AbortController()
          : null

        await new Promise((resolve) => setTimeout(resolve, 0))
        await new Promise((resolve) => requestAnimationFrame(resolve))

        const token = formData?.token || ''
        if (!token) {
          showToast('error', 'No se pudo obtener el token de la tarjeta. Intenta de nuevo.')
          setCheckoutState(CHECKOUT_STATE.FAILED_REQUIRES_NEW_CVV)
          setBrickResetKey((prev) => prev + 1)
          setIsProcessing(false)
          onProcessingChange?.(false)
          submitLockRef.current = false
          return Promise.reject(new Error('Token vacío'))
        }

        const clearProcessing = () => {
          setIsProcessing(false)
          onProcessingChange?.(false)
          submitLockRef.current = false
        }

        const logResult = (result) => {
          console.info('[PaymentFlow]', {
            flowId,
            flowType,
            result
          })
        }

        try {
          setCheckoutState(CHECKOUT_STATE.SUBMITTING_SUBSCRIPTION)
          if (saveCard) {
            const payerData = formData.payer || {}
            const identification = payerData.identification || {}
            const transactionAmount = (Number(amount) || VALIDATION_AMOUNT) / 100
            const payload = {
              transactionAmount,
              description: 'Validación de tarjeta',
              paymentMethodId: formData.payment_method_id || selectedPaymentMethod || '',
              payer: {
                email: payerData.email || '',
                firstName: payerData.first_name || '',
                lastName: payerData.last_name || '',
                ...(identification?.type && identification?.number && {
                  identification: {
                    type: identification.type,
                    number: identification.number
                  }
                })
              },
              token,
              installments: formData.installments || 1,
              issuerId: formData.issuer_id ? Number(formData.issuer_id) : undefined,
              cardName: cardNameRef?.current ?? 'Tarjeta',
              flowId,
              flowType,
              signal: activeRequestControllerRef.current?.signal
            }
            const requestPromise = onSaveCardRequest
              ? onSaveCardRequest(payload)
              : saveCardMutation.mutateAsync(payload)
            const response = await withTimeout(
              requestPromise,
              SUBMIT_TIMEOUT_MS,
              'La operación tardó demasiado. Intenta de nuevo.'
            )
            try {
              const result = onSuccess?.(response)
              const resultPromise = result != null && typeof result?.then === 'function' ? result : Promise.resolve()
              await withTimeout(resultPromise, SUBMIT_TIMEOUT_MS, 'La operación tardó demasiado. Intenta de nuevo.')
              if (!managedByParent) setShowSuccessMessage(true)
              setCheckoutState(CHECKOUT_STATE.SUCCESS)
              logResult('success')
            } catch (onSuccessError) {
              if (isTokenOrCvvError(onSuccessError)) {
                showToast('error', TOKEN_CVV_ERROR_MESSAGE)
                setCheckoutState(CHECKOUT_STATE.FAILED_REQUIRES_NEW_CVV)
                setBrickResetKey((prev) => prev + 1)
                logResult('token_error')
              } else {
                showToast('error', getErrorMessage(onSuccessError))
                setCheckoutState(CHECKOUT_STATE.IDLE)
                logResult('network_error')
              }
              return Promise.reject(onSuccessError)
            }
          } else {
            try {
              const payerData = formData.payer || {}
              const identification = payerData.identification || {}
              const payerForApi = {
                email: payerData.email || '',
                firstName: payerData.first_name || '',
                lastName: payerData.last_name || '',
                ...(identification?.type && identification?.number && {
                  identification: {
                    type: identification.type,
                    number: identification.number
                  }
                })
              }
              const preapprovalToken = pickPreapprovalToken(formData)
              const result = onSuccess?.({
                token,
                payerEmail: payerData.email || '',
                preapprovalToken,
                paymentMethodId: formData.payment_method_id || selectedPaymentMethod || '',
                payer: payerForApi,
                installments: formData.installments || 1,
                issuerId: formData.issuer_id ? Number(formData.issuer_id) : undefined,
                flowId,
                flowType,
                signal: activeRequestControllerRef.current?.signal
              })
              const resultPromise = result != null && typeof result?.then === 'function' ? result : Promise.resolve()
              await withTimeout(resultPromise, SUBMIT_TIMEOUT_MS, 'La operación tardó demasiado. Intenta de nuevo.')
              if (!managedByParent) setShowSuccessMessage(true)
              setCheckoutState(CHECKOUT_STATE.SUCCESS)
              logResult('success')
            } catch (onSuccessError) {
              if (isTokenOrCvvError(onSuccessError)) {
                showToast('error', TOKEN_CVV_ERROR_MESSAGE)
                setCheckoutState(CHECKOUT_STATE.FAILED_REQUIRES_NEW_CVV)
                setBrickResetKey((prev) => prev + 1)
                logResult('token_error')
              } else {
                showToast('error', getErrorMessage(onSuccessError))
                setCheckoutState(CHECKOUT_STATE.IDLE)
                logResult('network_error')
              }
              return Promise.reject(onSuccessError)
            }
          }
          return Promise.resolve()
        } catch (error) {
          if (isTokenOrCvvError(error)) {
            showToast('error', TOKEN_CVV_ERROR_MESSAGE)
            setCheckoutState(CHECKOUT_STATE.FAILED_REQUIRES_NEW_CVV)
            setBrickResetKey((prev) => prev + 1)
            logResult('token_error')
          } else {
            showToast('error', getErrorMessage(error))
            setCheckoutState(CHECKOUT_STATE.IDLE)
            logResult('network_error')
          }
          return Promise.reject(error)
        } finally {
          clearProcessing()
        }
      },
      onError: () => {
        activeRequestControllerRef.current?.abort?.()
        activeRequestControllerRef.current = null
        clearProcessing()
        setCheckoutState(CHECKOUT_STATE.IDLE)
      }
    }
  }
}

const PaymentForm = ({
  amount = 1,
  customerId,
  cardsIds = [],
  showSavedCard = false,
  paymentTypeId,
  savedCardCvvOnly = false,
  formId,
  hideSubmitButton = false,
  saveCard = false,
  isSubscription = false,
  onSuccess,
  onProcessingChange,
  onSaveCardRequest,
  securityMessage,
  brickId = 'payment',
  successMessage = 'Plan actualizado correctamente.',
  managedByParent = false,
  flowType,
  submitLabel
}) => {
  const containerRef = useRef(null)
  const paymentBrickControllerRef = useRef(null)
  const cardNameRef = useRef('Tarjeta')
  const onSuccessRef = useRef(onSuccess)
  const onProcessingChangeRef = useRef(onProcessingChange)
  const onSaveCardRequestRef = useRef(onSaveCardRequest)
  const showToastRef = useRef(null)
  const submitLockRef = useRef(false)
  const activeRequestControllerRef = useRef(null)
  const initAttemptRef = useRef(0)
  const pendingInitTimeoutRef = useRef(null)
  const secureFieldsInstanceRef = useRef(null)
  const securityCodeFieldRef = useRef(null)

  onSaveCardRequestRef.current = onSaveCardRequest
  const [isLoading, setIsLoading] = useState(true)
  const [isProcessing, setIsProcessing] = useState(false)
  const [showSuccessMessage, setShowSuccessMessage] = useState(false)
  const [checkoutState, setCheckoutState] = useState(CHECKOUT_STATE.IDLE)
  const [brickResetKey, setBrickResetKey] = useState(0)
  const saveCardMutation = useMutationSaveCard()
  const { showToast } = useContext(ToastContext)

  onSuccessRef.current = onSuccess
  onProcessingChangeRef.current = onProcessingChange
  showToastRef.current = showToast
  const effectiveAmount = saveCard ? (amount ?? VALIDATION_AMOUNT) : amount
  const savedCardId = savedCardCvvOnly ? getFirstSavedCardId(cardsIds) : ''
  const effectiveFlowType = flowType || (saveCard
    ? 'change_card'
    : (brickId === 'addonContract' ? 'addon_subscription' : 'plan_subscription'))

  const publicKey = isSubscription ? MERCADOPAGO_SUBSCRIPTION_PUBLIC_KEY : MERCADOPAGO_PAYMENT_PUBLIC_KEY

  useEffect(() => {
    let isDisposed = false
    const currentInitAttempt = ++initAttemptRef.current
    if (!publicKey) {
      setIsLoading(false)
      return
    }

    const initializePaymentBrick = async () => {
      try {
        if (isDisposed || currentInitAttempt !== initAttemptRef.current) return
        if (typeof window.MercadoPago === 'undefined') {
          setIsLoading(false)
          return
        }

        const mercadoPagoInstance = new window.MercadoPago(publicKey, {
          locale: 'es-CL'
        })

        if (savedCardCvvOnly) {
          if (!savedCardId) {
            setIsLoading(false)
            return
          }

          const fields = mercadoPagoInstance.fields

          if (!fields) {
            setIsLoading(false)
            showToast('error', 'No se pudo cargar el campo de seguridad. Intenta de nuevo.')
            return
          }

          if (!containerRef.current) {
            pendingInitTimeoutRef.current = setTimeout(() => {
              if (isDisposed || currentInitAttempt !== initAttemptRef.current) return
              if (containerRef.current) initializePaymentBrick()
              else setIsLoading(false)
            }, 100)
            return
          }

          clearBrickContainer(containerRef.current)
          const targetId = containerRef.current.id || `paymentBrick_${brickId}`
          const securityCodeField = fields.create('securityCode', {
            placeholder: '•••'
          })

          securityCodeField.mount(targetId)
          secureFieldsInstanceRef.current = fields
          securityCodeFieldRef.current = securityCodeField
          setCheckoutState(CHECKOUT_STATE.COLLECTING_CARD_DATA)
          setIsLoading(false)
          return
        }

        const bricksBuilder = mercadoPagoInstance.bricks()
        const settings = createPaymentBrickSettings({
          amount: effectiveAmount,
          customerId,
          cardsIds,
          showSavedCard,
          paymentTypeId,
          saveCard,
          submitLabel,
          cardNameRef,
          onSuccess: (result) => {
            return onSuccessRef.current?.(result)
          },
          onError: (error) => {
            console.error('Error al crear el formulario de pago:', error)
            return Promise.reject(error)
          },
          onProcessingChange: (isProcessingValue) => onProcessingChangeRef.current?.(isProcessingValue),
          setShowSuccessMessage,
          setIsProcessing,
          setCheckoutState,
          setBrickResetKey,
          flowType: effectiveFlowType,
          showToast: (toastType, message) => showToastRef.current?.(toastType, message),
          saveCardMutation,
          onSaveCardRequest: onSaveCardRequestRef.current
            ? (payload) => onSaveCardRequestRef.current(payload)
            : undefined,
          managedByParent,
          submitLockRef,
          activeRequestControllerRef
        })
        const previousOnReady = settings.callbacks.onReady
        settings.callbacks.onReady = () => {
          if (isDisposed || currentInitAttempt !== initAttemptRef.current) return
          previousOnReady?.()
          setIsLoading(false)
        }
        const previousOnError = settings.callbacks.onError
        settings.callbacks.onError = (error) => {
          if (isDisposed || currentInitAttempt !== initAttemptRef.current) return
          previousOnError?.(error)
          setIsLoading(false)
        }

        if (!containerRef.current) {
          pendingInitTimeoutRef.current = setTimeout(() => {
            if (isDisposed || currentInitAttempt !== initAttemptRef.current) return
            if (containerRef.current) initializePaymentBrick()
            else setIsLoading(false)
          }, 100)
          return
        }

        if (paymentBrickControllerRef.current) {
          try {
            paymentBrickControllerRef.current.unmount()
          } catch (_) {
            paymentBrickControllerRef.current = null
          }
          paymentBrickControllerRef.current = null
        }
        clearBrickContainer(containerRef.current)

        const targetId = containerRef.current.id || `paymentBrick_${brickId}`
        const newController = await bricksBuilder.create(
          'payment',
          targetId,
          settings
        )
        if (isDisposed || currentInitAttempt !== initAttemptRef.current) {
          try {
            newController?.unmount?.()
          } catch (_) {
            paymentBrickControllerRef.current = null
          }
          return
        }
        paymentBrickControllerRef.current = newController
      } catch (error) {
        if (isDisposed || currentInitAttempt !== initAttemptRef.current) return
        setIsLoading(false)
        showToast('error', 'Error al cargar el formulario de pago. Intenta de nuevo.')
      }
    }

    initializePaymentBrick()

    return () => {
      isDisposed = true
      initAttemptRef.current += 1
      if (pendingInitTimeoutRef.current) {
        clearTimeout(pendingInitTimeoutRef.current)
        pendingInitTimeoutRef.current = null
      }
      activeRequestControllerRef.current?.abort?.()
      activeRequestControllerRef.current = null
      submitLockRef.current = false
      if (securityCodeFieldRef.current) {
        try {
          securityCodeFieldRef.current.unmount?.()
          securityCodeFieldRef.current.destroy?.()
        } catch (_) {
          showToastRef.current?.('error', 'Error al cerrar el campo de seguridad.')
        } finally {
          securityCodeFieldRef.current = null
          secureFieldsInstanceRef.current = null
        }
      }
      if (paymentBrickControllerRef.current) {
        try {
          paymentBrickControllerRef.current.unmount()
          paymentBrickControllerRef.current = null
        } catch (_) {
          showToastRef.current?.('error', 'Error al cerrar el formulario de pago.')
        }
      }
      clearBrickContainer(containerRef.current)
    }
  }, [
    effectiveAmount,
    customerId,
    cardsIds,
    savedCardCvvOnly,
    showSavedCard,
    paymentTypeId,
    brickId,
    saveCard,
    managedByParent,
    isSubscription,
    publicKey,
    brickResetKey,
    effectiveFlowType,
    submitLabel,
    savedCardId
  ])

  const handleSavedCardCvvSubmit = async event => {
    event?.preventDefault?.()

    if (!savedCardId || !secureFieldsInstanceRef.current) {
      showToast('error', 'No se encontro la tarjeta para validar el CVV.')
      return
    }

    if (submitLockRef.current) {
      return
    }

    submitLockRef.current = true
    setIsProcessing(true)
    onProcessingChange?.(true)
    setCheckoutState(CHECKOUT_STATE.CREATING_TOKEN)

    const flowId = generateFlowId()
    activeRequestControllerRef.current?.abort?.()
    activeRequestControllerRef.current = typeof AbortController !== 'undefined'
      ? new AbortController()
      : null

    try {
      const tokenResponse = await withTimeout(
        secureFieldsInstanceRef.current.createCardToken({ cardId: savedCardId }),
        SUBMIT_TIMEOUT_MS,
        'La operacion tardo demasiado. Intenta de nuevo.'
      )

      if (!tokenResponse?.id) {
        throw new Error('No se pudo obtener el token de la tarjeta.')
      }

      setCheckoutState(CHECKOUT_STATE.SUBMITTING_SUBSCRIPTION)

      const result = onSuccessRef.current?.({
        token: tokenResponse.id,
        payerEmail: '',
        flowId,
        flowType: effectiveFlowType,
        signal: activeRequestControllerRef.current?.signal
      })
      const resultPromise =
        result != null && typeof result?.then === 'function' ? result : Promise.resolve()
      await withTimeout(
        resultPromise,
        SUBMIT_TIMEOUT_MS,
        'La operacion tardo demasiado. Intenta de nuevo.'
      )
      if (!managedByParent) setShowSuccessMessage(true)
      setCheckoutState(CHECKOUT_STATE.SUCCESS)
    } catch (error) {
      if (isTokenOrCvvError(error)) {
        showToast('error', TOKEN_CVV_ERROR_MESSAGE)
        setCheckoutState(CHECKOUT_STATE.FAILED_REQUIRES_NEW_CVV)
        setBrickResetKey(prev => prev + 1)
      } else {
        showToast('error', getErrorMessage(error))
        setCheckoutState(CHECKOUT_STATE.IDLE)
      }
    } finally {
      setIsProcessing(false)
      onProcessingChange?.(false)
      submitLockRef.current = false
    }
  }

  return (
    <div className="payment-form-shell relative w-full min-w-0 font-sans">
      {securityMessage && (
        <div className="mb-4 rounded-2xl border border-accent2 bg-accent2/5 px-4 py-3">
          <p className="text-sm font-sans">{securityMessage}</p>
        </div>
      )}
      {checkoutState === CHECKOUT_STATE.FAILED_REQUIRES_NEW_CVV && (
        <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm font-sans text-red-700">{TOKEN_CVV_ERROR_MESSAGE}</p>
        </div>
      )}

      {saveCard && (
        <div className="mb-4 w-full">
          <label className="mb-2 block text-sm font-sans font-semibold text-gray-700">
            Nombre de la tarjeta
          </label>
          <input
            type="text"
            onChange={(event) => {
              cardNameRef.current = event.target.value
            }}
            placeholder="Ejemplo: tarjeta empresa"
            className="payment-form-name-input w-full px-3 py-2  rounded-xl"
            disabled={isProcessing}
          />
        </div>
      )}

      {!managedByParent && isProcessing && (
        <div className="absolute inset-0 z-50 flex flex-col items-center justify-center rounded-[28px] bg-white/90">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-accent1 mb-4" />
          <h3 className="mb-2 text-xl font-sans font-bold text-gray-900">Procesando pago</h3>
          <p className="text-center font-sans text-gray-600">
            Por favor espera mientras validamos tu tarjeta...
          </p>
        </div>
      )}

      {!managedByParent && showSuccessMessage && (
        <div className="absolute inset-0 z-50 flex flex-col items-center justify-center rounded-[28px] bg-white">
          <div className="flex-shrink-0 w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
            <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="mb-2 text-xl font-sans font-bold text-gray-900">Listo</h3>
          <p className="text-center font-sans text-gray-600">{successMessage}</p>
        </div>
      )}

      {isLoading && (
        <div className="payment-form-loading flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-accent1" />
          <p className="ml-4 font-sans text-gray-600">Cargando formulario de pago...</p>
        </div>
      )}

      {savedCardCvvOnly ? (
        <form
          id={formId}
          className="space-y-4"
          onSubmit={handleSavedCardCvvSubmit}
        >
          <div>
            <label className="mb-2 block text-sm font-sans font-semibold text-slate-700">
              Codigo de seguridad (CVV)
            </label>
            <div className="payment-form-cvv-shell px-3 py-1.5">
              <div
                ref={containerRef}
                id={`paymentBrick_${brickId}_${brickResetKey}`}
                className="
                  payment-form-cvv-frame
                  h-[40px] w-full overflow-hidden
                  bg-gray-50
                  border border-slate-200
                  rounded-xl
                  pl-2
                  transition-all duration-200
                  focus-within:border-accent2
                  focus-within:ring-2
                  focus-within:ring-accent2/20
                "
              />
            </div>
            <p className="mt-1.5 text-xs font-sans text-slate-500">
              Los 3 digitos del reverso de tu tarjeta.
            </p>
          </div>

          {!hideSubmitButton && (
            <div className="flex justify-end">
              <CustomButton
                type="submit"
                title={isProcessing ? 'Validando...' : (submitLabel || 'Continuar')}
                color="accent2"
                className="rounded-full px-6 py-2"
                disabled={isProcessing || isLoading || !savedCardId}
              />
            </div>
          )}
        </form>
      ) : (
        <div className="payment-form-brick-shell">
          <div
            ref={containerRef}
            id={`paymentBrick_${brickId}_${brickResetKey}`}
            className="payment-form-brick-content min-h-[400px] w-full"
          />
        </div>
      )}
    </div>
  )
}

export default PaymentForm
